import { type ReactNode, useEffect } from 'react'
import { api } from '@/app/api'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { useLazyMeQuery } from '@/features/auth/auth-api'
import { markBootstrapped, setIdentity, logout } from '@/features/auth/auth-slice'
import { Logo } from '@/components/layout/logo'

/**
 * On first load, if we have a token, fetch the current identity (user +
 * company + permissions + enabled modules) before rendering the app. This
 * ensures access checks have data to work with.
 */
export function BootstrapGate({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch()
  const token = useAppSelector((s) => s.auth.token)
  const isBootstrapped = useAppSelector((s) => s.auth.isBootstrapped)
  const [fetchMe] = useLazyMeQuery()

  useEffect(() => {
    let cancelled = false

    async function bootstrap() {
      if (!token) {
        dispatch(markBootstrapped())
        return
      }
      try {
        const res = await fetchMe().unwrap()
        if (!cancelled) {
          dispatch(setIdentity({ user: res.user, company: res.company }))
        }
      } catch {
        if (!cancelled) {
          dispatch(logout())
          dispatch(api.util.resetApiState())
          dispatch(markBootstrapped())
        }
      }
    }

    void bootstrap()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (token && !isBootstrapped) {
    return <FullScreenLoader />
  }

  return <>{children}</>
}

function FullScreenLoader() {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 bg-background">
      <Logo />
      <div className="h-1 w-32 overflow-hidden rounded-full bg-muted">
        <div className="h-full w-1/3 animate-[loading_1s_ease-in-out_infinite] rounded-full bg-brand-terracotta" />
      </div>
      <style>{`@keyframes loading { 0% { transform: translateX(-100%) } 100% { transform: translateX(400%) } }`}</style>
    </div>
  )
}
